import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Menu, ChevronDown, Phone, Mail, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { SERVICES, getServicesByCategory } from '../data/servicesData';
import SocialIcons from './SocialIcons';
import './MobileMenu.css';

const MobileMenu = ({ onCalculatorOpen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    setActiveSubmenu(null);
  };

  const toggleSubmenu = (menu) => {
    setActiveSubmenu(activeSubmenu === menu ? null : menu);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setActiveSubmenu(null);
  };

  return (
    <>
      {/* Hamburger Button - Only visible on mobile */}
      <button
        onClick={toggleMenu}
        className={`mobile-menu-toggle ${isOpen ? 'tipc-hamburger-tap' : ''}`}
        aria-label="Toggle menu"
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Backdrop Overlay */}
      {isOpen && (
        <div 
          className="mobile-menu-backdrop"
          onClick={closeMenu}
        />
      )}

      {/* Mobile Menu Drawer */}
      <div className={`mobile-menu-drawer ${isOpen ? 'open tipc-mobile-menu-anim' : ''}`}>
        <div className="mobile-menu-content">
          {/* Header */}
          <div className="mobile-menu-header">
            <h2 className="mobile-menu-logo">EntoRid</h2>
            <button onClick={closeMenu} className="mobile-menu-close">
              <X size={24} />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="mobile-nav-list">
            <Link to="/" className="mobile-nav-item" onClick={closeMenu}>
              Home
            </Link>

            {/* Services Submenu — links to actual service pages */}
            <div className="mobile-nav-item-wrapper">
              <button
                className="mobile-nav-item with-submenu"
                onClick={() => toggleSubmenu('services')}
              >
                <span>Our Services</span>
                <ChevronDown
                  size={18}
                  className={`submenu-icon ${activeSubmenu === 'services' ? 'open' : ''}`}
                />
              </button>
              <div className={`mobile-submenu ${activeSubmenu === 'services' ? 'open' : ''}`}>
                <Link to="/services" className="mobile-submenu-item" onClick={closeMenu} style={{ fontWeight: 600 }}>
                  View All Services →
                </Link>
                {SERVICES.slice(0, 8).map(s => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="mobile-submenu-item"
                    onClick={closeMenu}
                  >
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Pricing submenu — same services with pricing page scroll */}
            <div className="mobile-nav-item-wrapper">
              <button
                className="mobile-nav-item with-submenu"
                onClick={() => toggleSubmenu('pricing')}
              >
                <span>Pricing</span>
                <ChevronDown size={18} className={`submenu-icon ${activeSubmenu === 'pricing' ? 'open' : ''}`} />
              </button>
              <div className={`mobile-submenu ${activeSubmenu === 'pricing' ? 'open' : ''}`}>
                {getServicesByCategory('termite').map(s => (
                  <Link key={s.slug} to={`/pricing/${s.slug}`} className="mobile-submenu-item" onClick={closeMenu}>
                    {s.name}
                  </Link>
                ))}
                {getServicesByCategory('general').map(s => (
                  <Link key={s.slug} to={`/pricing/${s.slug}`} className="mobile-submenu-item" onClick={closeMenu}>
                    {s.name}
                  </Link>
                ))}
                {getServicesByCategory('specialized').map(s => (
                  <Link key={s.slug} to={`/pricing/${s.slug}`} className="mobile-submenu-item" onClick={closeMenu}>
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Gallery Submenu */}
            <div className="mobile-nav-item-wrapper">
              <button
                className="mobile-nav-item with-submenu"
                onClick={() => toggleSubmenu('gallery')}
              >
                <span>Gallery</span>
                <ChevronDown 
                  size={18} 
                  className={`submenu-icon ${activeSubmenu === 'gallery' ? 'open' : ''}`}
                />
              </button>
              <div className={`mobile-submenu ${activeSubmenu === 'gallery' ? 'open' : ''}`}>
                <Link to="/gallery/images" className="mobile-submenu-item" onClick={closeMenu}>Before & After Images</Link>
                <Link to="/gallery/videos" className="mobile-submenu-item" onClick={closeMenu}>Treatment Videos</Link>
              </div>
            </div>

            {/* Service Locations Submenu */}
            <div className="mobile-nav-item-wrapper">
              <button
                className="mobile-nav-item with-submenu"
                onClick={() => toggleSubmenu('locations')}
              >
                <span>Service Locations</span>
                <ChevronDown 
                  size={18} 
                  className={`submenu-icon ${activeSubmenu === 'locations' ? 'open' : ''}`}
                />
              </button>
              <div className={`mobile-submenu ${activeSubmenu === 'locations' ? 'open' : ''}`}>
                <a href="#" className="mobile-submenu-item" onClick={closeMenu}>Dehradun</a>
                <a href="#" className="mobile-submenu-item" onClick={closeMenu}>Rishikesh</a>
                <a href="#" className="mobile-submenu-item" onClick={closeMenu}>Haridwar</a>
                <a href="#" className="mobile-submenu-item" onClick={closeMenu}>Roorkee</a>
                <a href="#" className="mobile-submenu-item" onClick={closeMenu}>All Locations</a>
              </div>
            </div>

            {/* Testimonials */}
            <div className="mobile-nav-item-wrapper">
              <button
                className="mobile-nav-item with-submenu"
                onClick={() => toggleSubmenu('testimonials')}
              >
                <span>Testimonials</span>
                <ChevronDown
                  size={18}
                  className={`submenu-icon ${activeSubmenu === 'testimonials' ? 'open' : ''}`}
                />
              </button>
              <div className={`mobile-submenu ${activeSubmenu === 'testimonials' ? 'open' : ''}`}>
                <Link to="/testimonials/reviews" className="mobile-submenu-item" onClick={closeMenu}>Customer Reviews</Link>
                <Link to="/testimonials/videos" className="mobile-submenu-item" onClick={closeMenu}>Video Testimonials</Link>
              </div>
            </div>

            {/* Blog & News */}
            <Link to="/blog" className="mobile-nav-item" onClick={closeMenu} data-testid="mobile-nav-blog">
              Blog &amp; News
            </Link>

            {/* Contact CTA */}
            <Button 
              onClick={() => {
                closeMenu();
                if (onCalculatorOpen) onCalculatorOpen();
              }}
              className="mobile-nav-cta"
            >
              <Phone className="mr-2" size={18} />
              Contact Us
            </Button>
          </nav>

          {/* Contact Info Footer */}
          <div className="mobile-menu-footer">
            <a href="tel:+917819988010" className="mobile-contact-item" onClick={closeMenu}>
              <Phone size={16} />
              <span>+91-7819988010</span>
            </a>
            <a href="mailto:info.entorid@gmail.com" className="mobile-contact-item" onClick={closeMenu}>
              <Mail size={16} />
              <span>info.entorid@gmail.com</span>
            </a>
            <a href="https://api.whatsapp.com/send?phone=919410110910" className="mobile-contact-item" target="_blank" rel="noopener noreferrer" onClick={closeMenu}>
              <MessageCircle size={16} />
              <span>WhatsApp +91-9410110910</span>
            </a>
            <div className="pt-3 mt-3 border-t border-slate-200">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Follow EntoRid&trade;</p>
              <SocialIcons variant="outline" size="md" className="justify-start" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;